import { memo, useState, useEffect, useCallback } from 'react'
import { useLazyQuery } from '@apollo/client'
import { GET_AD_CREATIVES_WITH_ASSETS } from '@/graphql/ad-creatives-library-queries'
import { FilterSection, CreativesList } from '@/pages/campaigns/creatives/components'
import { HeaderIcon } from '@/components/icons'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import _ from 'lodash'

const AdCreativeSelection = memo(
    ({
        audience,
        stage,
        selectedCreatives = [],
        onCreativeSelect,
        onBulkCreativeSelect,
        leadSource = 'instant_form',
        campaignId,
    }) => {
        const [searchTerm, setSearchTerm] = useState('')
        const [filterFormats, setFilterFormats] = useState(new Set())
        const [creatives, setCreatives] = useState([])
        const [isLoading, setIsLoading] = useState(false)
        const [error, setError] = useState(null)
        const { getStepContentData } = useWizard()
        const { selectedObjective } = getStepContentData('campaignObjective')

        const [fetchCreatives, { loading: queryLoading }] = useLazyQuery(GET_AD_CREATIVES_WITH_ASSETS, {
            onCompleted: (data) => {
                const creativesData = data?.AdCreative?.searchFilterAdCreativesJoinAssets || []
                setCreatives(creativesData)
                setIsLoading(false)
            },
            onError: (error) => {
                console.error('Error fetching creatives:', error) // eslint-disable-line no-console
                setError('Failed to load creatives. Please try again.')
                setIsLoading(false)
            },
        })

        // Fetch creatives when component mounts or filters change
        useEffect(() => {
            const timer = setTimeout(() => {
                const formats = filterFormats.size > 0 ? Array.from(filterFormats) : null
                const searchQuery = searchTerm.length >= 2 ? searchTerm : null

                setIsLoading(true)
                fetchCreatives({
                    variables: {
                        searchTerm: searchQuery,
                        formats: formats,
                        campaignId: campaignId,
                    },
                })
            }, 300)

            return () => clearTimeout(timer)
        }, [searchTerm, filterFormats, fetchCreatives, campaignId])

        // Initial load
        useEffect(() => {
            setIsLoading(true)
            fetchCreatives({
                variables: {
                    searchTerm: null,
                    formats: null,
                    campaignId: campaignId,
                },
            })
        }, [fetchCreatives, campaignId])

        const handleSearchChange = useCallback((value) => {
            setSearchTerm(value)
        }, [])

        const handleFormatToggle = useCallback(
            (format) => {
                const newFormats = new Set(filterFormats)
                if (newFormats.has(format)) {
                    newFormats.delete(format)
                } else {
                    newFormats.add(format)
                }
                setFilterFormats(newFormats)
            },
            [filterFormats],
        )

        const handleClearFormatFilters = useCallback(() => {
            setFilterFormats(new Set())
        }, [])

        const handleApplyFilters = useCallback((newFormats) => {
            setFilterFormats(new Set(newFormats))
        }, [])

        const isCreativeSelected = useCallback(
            (creativeId) => {
                return selectedCreatives.includes(creativeId)
            },
            [selectedCreatives],
        )

        const handleToggleSelection = useCallback(
            (creativeId) => {
                onCreativeSelect(creativeId)
            },
            [onCreativeSelect],
        )

        // Calculate filtered creatives before using in callbacks
        const filteredCreatives = creatives.filter((creative) => {
            // Filter out creatives with failed or processing status
            if (creative.status === 'FAILED' || creative.status === 'PROCESSING') {
                return false
            }

            // Filter based on lead source for leads campaigns
            if (selectedObjective === 'leads') {
                if (leadSource === 'instant_form') {
                    // Show only creatives with lead forms attached
                    return !!creative.lead_form_id
                } else if (leadSource === 'website_form') {
                    // Show only creatives without lead forms (website forms)
                    return !creative.lead_form_id
                }
            } else {
                // For all non-leads campaigns, hide creatives with lead forms
                if (creative.lead_form_id) {
                    return false
                }
            }

            // Filter out creatives without asset variations (matches CreativeCard logic)
            const assetVariations = creative.adAssets?.map((asset) => asset.variations).flat() || []
            if (assetVariations.length === 0) {
                return false
            }

            return true
        })

        const handleSelectAll = useCallback(() => {
            // Select all currently visible (filtered) creatives
            if (!audience || !stage) return

            if (filteredCreatives && filteredCreatives.length > 0) {
                // Get all creative IDs from filtered creatives only
                const allCreativeIds = filteredCreatives
                    .filter((creative) => creative && creative.id)
                    .map((creative) => creative.id)

                // Use bulk select if available, otherwise fall back to individual selection
                if (onBulkCreativeSelect) {
                    onBulkCreativeSelect(allCreativeIds)
                } else {
                    // Fallback to individual selection (though this may have issues with batching)
                    const creativesToSelect = allCreativeIds.filter((id) => !selectedCreatives.includes(id))
                    creativesToSelect.forEach((creativeId) => {
                        onCreativeSelect(creativeId)
                    })
                }
            }
        }, [filteredCreatives, selectedCreatives, onCreativeSelect, onBulkCreativeSelect, audience, stage, creatives])

        const isLoadingState = isLoading || queryLoading
        const hasActiveFilters = searchTerm.length >= 2 || filterFormats.size > 0

        return (
            <div className="tw:border-t tw:border-gray-600 tw:pt-6 tw:bg-[#071727]">
                {/* Header */}
                <div className="tw:px-6 tw:mb-4 tw:flex tw:items-center tw:gap-2">
                    <HeaderIcon />
                    <span className="tw:text-white tw:!font-semibold tw:!text-lg">
                        Select Ad Creatives for {audience.name} ({stage.name} Stage)
                    </span>
                </div>
                <p className="tw:text-gray-400 tw:text-sm tw:px-6 ">
                    {selectedCreatives.length} creative{selectedCreatives.length !== 1 ? 's' : ''} selected
                </p>

                {/* Filter Section */}
                <div className="tw:px-6 tw:mb-4">
                    <FilterSection
                        searchTerm={searchTerm}
                        onSearchChange={handleSearchChange}
                        filterFormats={filterFormats}
                        onFormatToggle={handleFormatToggle}
                        onClearFormatFilters={handleClearFormatFilters}
                        onApplyFilters={handleApplyFilters}
                        selectionCount={selectedCreatives.length}
                        showSelectAll={true}
                        onSelectAll={handleSelectAll}
                    />
                </div>

                {/* Creatives List */}
                <div className="tw:px-6">
                    <CreativesList
                        isLoading={isLoadingState}
                        error={error}
                        filteredCreatives={filteredCreatives}
                        hasActiveFilters={hasActiveFilters}
                        isCreativeSelected={isCreativeSelected}
                        onToggleSelection={handleToggleSelection}
                    />
                </div>
            </div>
        )
    },
)

AdCreativeSelection.displayName = 'AdCreativeSelection'

export default AdCreativeSelection
